import { useEffect, useRef } from 'react';
import { useTranslations } from 'use-intl';
import { MessageSquarePlus, Trash2, X } from 'lucide-react';
import { formatDate } from '@/lib/format';
import type { Locale } from '@/i18n';
import { Button } from '@/ui/Button';

export interface ThreadRow {
  id: string;
  title: string;
  lastMessageAt: number;
}

/**
 * The conversations, as a list you read rather than a panel you scroll past.
 *
 * On a phone it was stacked above the conversation, so reaching the assistant
 * meant scrolling the whole history of what you had asked it — and the more you
 * used it the further away it got. It is a drawer there, and a rail beside the
 * conversation where there is room for one.
 */
function ThreadItems({
  threads,
  activeId,
  locale,
  onOpen,
  onDelete,
}: {
  threads: ThreadRow[];
  activeId: string | null;
  locale: Locale;
  onOpen: (id: string) => void;
  onDelete: (id: string) => void;
}) {
  const t = useTranslations();

  if (threads.length === 0) {
    return <p className="px-4 py-6 text-meta text-ink-muted">{t('chat.noThreads')}</p>;
  }

  return (
    <ul className="divide-y divide-line">
      {threads.map((th) => (
        <li key={th.id} className="flex items-stretch">
          <button
            type="button"
            onClick={() => onOpen(th.id)}
            aria-current={th.id === activeId ? 'true' : undefined}
            className={`min-w-0 flex-1 px-4 py-3 text-start transition-colors hover:bg-surface-sunken ${
              th.id === activeId ? 'bg-signal-soft' : ''
            }`}
          >
            {/* Two lines, not one truncated one: a question is the only name a
                conversation has, and "Why is Thursday heavi…" is not it. */}
            <span className="line-clamp-2 block leading-snug font-medium">{th.title}</span>
            <span className="mt-0.5 block text-meta text-ink-faint">
              {formatDate(new Date(th.lastMessageAt).toISOString().slice(0, 10), locale)}
            </span>
          </button>
          {/* A real target, and it says what it does. It was a 24px "×" with
              nothing to tell a screen reader what it removed. */}
          <button
            type="button"
            aria-label={t('chat.deleteThread', { title: th.title })}
            onClick={() => onDelete(th.id)}
            className="grid w-11 shrink-0 place-items-center text-ink-faint transition-colors hover:text-risk focus-visible:text-risk"
          >
            <Trash2 aria-hidden="true" className="size-4" />
          </button>
        </li>
      ))}
    </ul>
  );
}

export function ThreadRail(props: {
  threads: ThreadRow[];
  activeId: string | null;
  locale: Locale;
  onOpen: (id: string) => void;
  onNew: () => void;
  onDelete: (id: string) => void;
}) {
  const t = useTranslations();
  return (
    <aside className="hidden h-full min-h-0 flex-col rounded-(--radius-panel) border border-line bg-surface shadow-(--shadow-panel) lg:flex">
      <div className="flex items-center gap-2 border-b border-line px-4 py-3">
        <h2 className="font-condensed text-subhead font-semibold tracking-tight">
          {t('chat.threads')}
        </h2>
        <span className="tabular text-meta text-ink-faint">{props.threads.length || ''}</span>
        <Button
          variant="secondary"
          className="ms-auto !px-3"
          onClick={props.onNew}
        >
          <MessageSquarePlus aria-hidden="true" className="size-4" />
          {t('chat.newThread')}
        </Button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        <ThreadItems {...props} />
      </div>
    </aside>
  );
}

/** The same list, over the conversation, on a screen with room for one thing. */
export function ThreadDrawer({
  open,
  onClose,
  ...props
}: {
  open: boolean;
  onClose: () => void;
  threads: ThreadRow[];
  activeId: string | null;
  locale: Locale;
  onOpen: (id: string) => void;
  onNew: () => void;
  onDelete: (id: string) => void;
}) {
  const t = useTranslations();
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const previous = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.documentElement.style.overflow = previous;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-40 lg:hidden">
      <div
        className="absolute inset-0 bg-ink/40"
        onClick={onClose}
        role="presentation"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('chat.threads')}
        className="absolute inset-y-0 start-0 flex w-[88%] max-w-sm flex-col bg-surface shadow-(--shadow-sheet)"
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
      >
        <div className="flex items-center gap-2 border-b border-line px-4 py-3">
          <h2 className="font-condensed text-subhead font-semibold tracking-tight">
            {t('chat.threads')}
          </h2>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
            className="-me-2 ms-auto grid size-11 place-items-center text-ink-muted hover:text-ink"
          >
            <X aria-hidden="true" className="size-5" />
          </button>
        </div>
        <div className="border-b border-line p-3">
          <Button variant="secondary" className="w-full" onClick={props.onNew}>
            <MessageSquarePlus aria-hidden="true" className="size-4" />
            {t('chat.newThread')}
          </Button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
          <ThreadItems {...props} />
        </div>
      </div>
    </div>
  );
}
